import { useEffect } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { useHighlighted } from "contexts/highlighted";
import { shownActions, useDispatchShown, useLazyShown } from "contexts/shown";

import { ObjectVisibility } from "..";
import { selectDefaultVisibility } from "../renderSlice";

/**
 * Keeps the set of objects that stay at natural appearance while the rest of the scene is faded.
 *
 * Registered in `StandaloneOnlyHooks`. Procore keeps the equivalent set in the
 * TransparentSelectionBase highlight collection, because its Isolate and X-ray actions build on
 * it — see {@link useUpdateTransparentSelectionBase}.
 */
export function useUpdateShown() {
    const visibility = useAppSelector(selectDefaultVisibility);
    const dispatchShown = useDispatchShown();
    const lazyShown = useLazyShown();
    const highlightedIdArr = useHighlighted().idArr;

    useEffect(() => {
        // Standalone has no Isolate, so the set is only ever grown from the selection, and it is
        // kept when visibility returns to neutral — it is cleared explicitly (reset view, new
        // scene, viewpoints, removing from the selection basket) rather than by leaving the mode.
        const foldsSelection =
            visibility === ObjectVisibility.Transparent || visibility === ObjectVisibility.SemiTransparent;
        if (!foldsSelection) {
            return;
        }

        // Fold the current selection into the Shown set so it keeps rendering at natural
        // appearance after it's later deselected.
        const moreIds = highlightedIdArr.filter((id) => !lazyShown.current.ids.has(id));
        if (moreIds.length === 0) {
            return;
        }

        dispatchShown(shownActions.add(moreIds));
    }, [dispatchShown, highlightedIdArr, lazyShown, visibility]);
}
